import React from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";

import Box from "@amzn/meridian/esm/components/box";
import Column from "@amzn/meridian/esm/components/column";
import Divider from "@amzn/meridian/esm/components/divider";
import Row from "@amzn/meridian/esm/components/row";
import Icon from "@amzn/meridian/esm/components/icon";
import Text from "@amzn/meridian/esm/components/text";
import Theme from "@amzn/meridian/esm/components/theme";

import calendarTokens from "@amzn/meridian-tokens/base/icon/calendar";
import alertFilledTokens from "@amzn/meridian-tokens/base/icon/alert-knockout";
import errorKnockout from "@amzn/meridian-tokens/base/icon/error-knockout";

import AimsMasthead from "../../common/AimsMasthead";
import SummaryBodyInDetailPage from "../../common/summary/SummaryBodyInDetailPage";
import SummaryFeedback from "../../common/feedback/SummaryFeedback";
import SummaryListButton from "../../common/summary/SummaryListButton";
import SummaryNotApprovedFeedback from "../../common/feedback/SummaryNotApprovedFeedback";
import { SUMMARY_FEEDBACK_STATUS, SUMMARY_TYPES } from "../../common/summary/constants";

import { TABS } from "../../../constants";
import LogManager from "../../utils/LogManager";
import AttendeeListInDetailPage from "../../common/summary/AttendeeListInDetailPage";

/**
 * SummaryDetail Component
 *
 * This component displays the full detail view of a selected meeting summary.
 * It is rendered when a user navigates to the summary detail page from the summary list.
 *
 * Key Features:
 * - Shows a "Summary List" button to return to the main summary tab
 * - Displays meeting title and date/time
 * - Conditionally renders different content based on summary type:
 *   - Special cases (e.g., short meeting, server error, P&C, etc.) are handled inside <SummaryContent />
 *   - If valid, displays narrative summary, decisions, next steps, and a footer note
 * - Includes optional feedback section if no feedback has been submitted
 *
 */
const SummaryDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const summary = location.state?.summary;

  const summaryFeedbackStatus = SUMMARY_TYPES.APPROVED.includes(summary?.summaryType) 
    ? SUMMARY_FEEDBACK_STATUS.APPROVED
    : SUMMARY_TYPES.NOT_APPROVED.includes(summary?.summaryType)
    ? SUMMARY_FEEDBACK_STATUS.NOT_APPROVED
    : SUMMARY_FEEDBACK_STATUS.OTHER;

  const handleTabChange = (tabKey) => {
    navigate("/", { state: { selectedTab: tabKey } });
  };

  const onSummaryList = () => {
    navigate("/", { state: { selectedTab: TABS.SUMMARIES } });
  };

  if (!summary) {
    LogManager.error("No summary in SummaryDetail view. This should not happen.");
    return (
      <Column>
        <Text type="b200" color="error">
          Summary not found.
        </Text>
      </Column>
    );
  }

  return (
    <Column spacing="none" heights={["fill", "fit", "fill"]}>
      <Column spacing="none">
        {/* AIMS top masthead bar */}
        <AimsMasthead selectedTab={TABS.SUMMARIES} setSelectedTab={handleTabChange} />
        <Column height="40px" />

        {/* "Summary list" link button */}
        <SummaryListButton onSummaryListInClient={onSummaryList} />

        <Column height="24px" />
      </Column>

      <Row widths={["32px", "326px", "fill"]} spacing="none" spacingInset="none">
        <Row width="32px" />

        {/*Scroll bar section*/}
        <Box width="326px" height="68vh" spacing="none" spacingInset="none" overflowY="scroll">
          <Column spacing="none" spacingInset="none" width="306px">
            {/* Summary title */}
            <>
              <style>
                {`
                  @font-face {
                    font-family: 'Amazon Ember Serif';
                    src: url("https://m.media-amazon.com/images/G/01/kindle/rewards/home/2022_beta/fonts/AmazonEmberSerif_Bd.woff") format("woff"), 
                      url("https://m.media-amazon.com/images/G/01/kindle/rewards/home/2022_beta/fonts/AmazonEmberSerif_Bd.woff") format("woff2");
                    font-weight: 700;
                  }
                `}
              </style>
              <Theme tokens={{ textDefaultFontFamily: ["Amazon Ember Serif"] }}>
                <Text type="h200">
                  {summary.meetingTitle}
                </Text>
              </Theme>
            </>
            <Column height="20px" />

            {/* Summary time with calendar icon */}
            <Row spacing="none" spacingInset="none">
              <Icon tokens={calendarTokens} color="secondary" />
              <Row width="4px" />
              <Text type="b100" color="secondary" fontFamily="amazonEmber" className="summary-time">
                {summary.meetingDateAndTime}
              </Text>
            </Row>

            <Column height="8px" />

            {/* Attendee list */}
            <AttendeeListInDetailPage attendeeNameList={summary.attendeeNameList} />

            {/*Divider*/}
            <Column height="16px" />
            <Divider spacingBefore="0" spacingAfter="0" />
            <Column height="16px" />

            <SummaryBodyInDetailPage summary={summary} isTopicNotApproved={summaryFeedbackStatus === SUMMARY_FEEDBACK_STATUS.NOT_APPROVED}/>
          </Column>
        </Box>
      </Row>

      {/* Feedback */}
      {(!summary.feedback && !summary.feedbackComment) && (
        <Box width="358px">
          {summaryFeedbackStatus === SUMMARY_FEEDBACK_STATUS.APPROVED ? (
            <SummaryFeedback aimsUuid={id} />
          ) : summaryFeedbackStatus === SUMMARY_FEEDBACK_STATUS.NOT_APPROVED ? (
            <SummaryNotApprovedFeedback aimsUuid={id} />
          ) : null
          }
        </Box>
      )}
    </Column>
  );
};

export default SummaryDetail;
